import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  BuildingOfficeIcon, 
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { ArrowLeft, Save, Users, Clock, Trash2, Plus, UserCog } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { useToast } from '../../components/ui/use-toast';

import { useAuthStore } from '../../store/authStore';
import { api } from '../../services/api';
import StaffDetailsRows from '../../components/organizations/StaffDetailsRows';
import { emptyStaffRow, toApiStaffRows } from '../../lib/staffDetails';

const NewOrganizationPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      start_date: new Date().toISOString().split('T')[0],
      ambulance_count: 0,
      is_multiple_organization: false
    }
  });

  const [teamAssignments, setTeamAssignments] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);

  // Institution autocomplete state
  const [institutionInput, setInstitutionInput] = useState('');
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [institutionLoading, setInstitutionLoading] = useState(false);

  useEffect(() => {
    if (institutionInput.length < 3) {
      setInstitutionSuggestions([]);
      setShowInstitutionDropdown(false);
      return;
    }
    let cancelled = false;
    setInstitutionLoading(true);
    api.get('/institutions', { params: { search: institutionInput } })
      .then(res => {
        if (!cancelled) {
          const list = res.data?.data || res.data || [];
          setInstitutionSuggestions(Array.isArray(list) ? list : []);
          setShowInstitutionDropdown(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setInstitutionLoading(false); });
    return () => { cancelled = true; };
  }, [institutionInput]);

  const addStaffRow = () => {
    setStaffDetails(prev => [...prev, emptyStaffRow()]);
  };

  const removeStaffRow = (index) => {
    setStaffDetails(prev => prev.filter((_, i) => i !== index));
  };

  const updateStaffRow = (index, field, value) => {
    setStaffDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const ambulanceCount = parseInt(watch('ambulance_count') || 0);

  // Fetch organization types
  const { data: typesData } = useQuery({
    queryKey: ['organization-types'],
    queryFn: () => api.get('/organizations/lookup/types').then(res => res.data),
  });

  // Fetch available teams
  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/teams').then(res => res.data),
  });

  const organizationTypes = typesData?.data || [];
  const availableTeams = teamsData?.data || [];

  // Update team assignments when ambulance count changes
  useEffect(() => {
    const count = parseInt(ambulanceCount) || 0;
    setTeamAssignments(prev => {
      if (count > prev.length) {
        const newAssignments = [...prev];
        for (let i = prev.length; i < count; i++) {
          newAssignments.push({
            team_id: '',
            assignment_date: watch('start_date') || '',
            assignment_time: watch('start_time') || '',
            end_time: watch('end_time') || '',
            vehicle_plate: '',
            notes: ''
          });
        }
        return newAssignments;
      } else if (count < prev.length) {
        return prev.slice(0, count);
      }
      return prev;
    });
  }, [ambulanceCount]);

  const updateTeamAssignment = (index, field, value) => {
    setTeamAssignments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Create organization mutation
  const createOrganizationMutation = useMutation({
    mutationFn: (data) => api.post('/organizations', data),
    onSuccess: (response) => {
      toast({
        title: "Başarılı",
        description: "Organizasyon başarıyla oluşturuldu",
      });
      queryClient.invalidateQueries(['organizations']);
      navigate('/organizations');
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Organizasyon oluşturulamadı",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data) => {
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key, 
        value === '' ? null : value
      ])
    );

    cleanedData.team_assignments = teamAssignments.filter(ta => ta.team_id);
    cleanedData.staff_details = toApiStaffRows(staffDetails);
    createOrganizationMutation.mutate(cleanedData);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/organizations')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Organizasyonlara Dön
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Yeni Organizasyon</h1>
            <p className="text-muted-foreground">
              Yeni etkinlik organizasyonu oluşturun
            </p>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="h-5 w-5 text-primary" />
                  <CardTitle>Organizasyon Bilgileri</CardTitle>
                </div>
                <CardDescription>
                  Temel organizasyon detaylarını girin
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="organization_name" className="flex items-center space-x-1">
                      <span>Organizasyon Adı *</span>
                    </Label>
                    <Input
                      id="organization_name"
                      {...register('organization_name', { required: 'Organizasyon adı gerekli' })}
                      className={errors.organization_name ? 'border-destructive' : ''}
                      placeholder="Organizasyon adını girin"
                    />
                    {errors.organization_name && (
                      <p className="text-sm text-destructive">{errors.organization_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization_type">Organizasyon Türü</Label>
                    <select
                      id="organization_type"
                      {...register('organization_type')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Tür seçin</option>
                      {organizationTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="doctor_status">Doktor Durumu</Label>
                    <select
                      id="doctor_status"
                      {...register('doctor_status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Seçin</option>
                      <option value="Gerekli">Gerekli</option>
                      <option value="Gerekli Değil">Gerekli Değil</option>
                      <option value="Standby">Standby</option>
                    </select>
                  </div>
                </div>

                <Separator />

                {/* Authorized Person Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center space-x-2">
                    <UserIcon className="h-5 w-5" />
                    <span>Yetkili Kişi Bilgileri</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="authorized_person">Yetkili Kişi</Label>
                      <Input
                        id="authorized_person"
                        {...register('authorized_person')}
                        placeholder="Yetkili kişi adını girin"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="authorized_phone">Yetkili Telefon</Label>
                      <Input
                        id="authorized_phone"
                        {...register('authorized_phone')}
                        placeholder="Telefon numarası"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="authorized_description">Yetkili Açıklama</Label>
                    <Textarea
                      id="authorized_description"
                      {...register('authorized_description')}
                      placeholder="Yetkili kişi hakkında açıklama"
                      rows={3}
                    />
                  </div>
                </div>

                <Separator />

                {/* Timing Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5" />
                    <span>Tarih ve Saat Bilgileri</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                      <Input
                        type="date"
                        id="start_date"
                        {...register('start_date')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="start_time">Başlangıç Saati</Label>
                      <Input
                        type="time"
                        id="start_time"
                        {...register('start_time')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end_date">Bitiş Tarihi</Label>
                      <Input
                        type="date"
                        id="end_date"
                        {...register('end_date')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="end_time">Bitiş Saati</Label>
                      <Input
                        type="time"
                        id="end_time"
                        {...register('end_time')}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Financial and Operational Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center space-x-2">
                    <CurrencyDollarIcon className="h-5 w-5" />
                    <span>Mali ve Operasyonel Detaylar</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="amount">Tutar (TL)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        id="amount"
                        {...register('amount')}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="doctor_price">Doktor Ücreti (TL)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        id="doctor_price"
                        {...register('doctor_price')}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="protocol_112_no">112 P. No</Label>
                      <Input
                        id="protocol_112_no"
                        {...register('protocol_112_no')}
                        placeholder="Protokol numarası"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="ambulance_count">Ambulans Sayısı</Label>
                      <Input
                        type="number"
                        min="0"
                        id="ambulance_count"
                        {...register('ambulance_count')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vehicle_number">Araç No</Label>
                    <Input
                      id="vehicle_number"
                      {...register('vehicle_number')}
                      placeholder="Araç numarası"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_multiple_organization"
                      {...register('is_multiple_organization')}
                      className="h-4 w-4 text-primary border-gray-300 rounded"
                    />
                    <Label htmlFor="is_multiple_organization">Çoklu Organizasyon</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Assignments */}
            {ambulanceCount > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <UserGroupIcon className="h-5 w-5 text-primary" />
                    <CardTitle>Ekip Atamaları</CardTitle>
                  </div>
                  <CardDescription>
                    {ambulanceCount} ambulans için ekip atamalarını yapın
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {teamAssignments.map((assignment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50/50">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                          <Users className="h-4 w-4" />
                          <span>Ambulans {index + 1}</span>
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2 space-y-1">
                          <Label className="text-xs">Ekip Seç</Label>
                          <select
                            value={assignment.team_id}
                            onChange={(e) => updateTeamAssignment(index, 'team_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="">Ekip seçin</option>
                            {availableTeams.map((team) => (
                              <option
                                key={team.id}
                                value={team.id}
                                disabled={teamAssignments.some((ta, i) => i !== index && ta.team_id === String(team.id))}
                              >
                                {team.code} - {team.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Başlangıç Saati</span>
                          </Label>
                          <Input
                            type="time"
                            value={assignment.assignment_time}
                            onChange={(e) => updateTeamAssignment(index, 'assignment_time', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Bitiş Saati</span>
                          </Label>
                          <Input
                            type="time"
                            value={assignment.end_time}
                            onChange={(e) => updateTeamAssignment(index, 'end_time', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Araç Plakası</Label>
                          <Input
                            value={assignment.vehicle_plate}
                            onChange={(e) => updateTeamAssignment(index, 'vehicle_plate', e.target.value)}
                            placeholder="Araç plakası"
                            className="h-9 text-sm"
                          />
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Not</Label>
                          <Input
                            value={assignment.notes}
                            onChange={(e) => updateTeamAssignment(index, 'notes', e.target.value)}
                            placeholder="Ekip notu"
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          {/* Staff Details */}
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserCog className="h-5 w-5 text-primary" />
                  <CardTitle>Ek Personel Detayları</CardTitle>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addStaffRow}>
                  <Plus className="h-4 w-4 mr-1" />
                  Satır Ekle
                </Button>
              </div>
              <CardDescription>
                Faaliyet raporunda görünecek ek personel bilgilerini girin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StaffDetailsRows
                rows={staffDetails}
                onChange={updateStaffRow}
                onRemove={removeStaffRow}
              />
            </CardContent>
          </Card>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Address Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <MapPinIcon className="h-5 w-5 text-primary" />
                  <CardTitle>Adres Bilgileri</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="province">İl</Label>
                  <Input
                    id="province"
                    {...register('province')}
                    placeholder="İl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="district">İlçe</Label>
                  <Input
                    id="district"
                    {...register('district')}
                    placeholder="İlçe"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="neighborhood">Mahalle</Label>
                  <Input
                    id="neighborhood"
                    {...register('neighborhood')}
                    placeholder="Mahalle"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="street">Cadde/Sokak</Label>
                  <Input
                    id="street"
                    {...register('street')}
                    placeholder="Cadde/Sokak"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adres</Label>
                  <Textarea
                    id="address"
                    {...register('address')}
                    placeholder="Detaylı adres"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_description">Adres Tarifi</Label>
                  <Textarea
                    id="address_description"
                    {...register('address_description')}
                    placeholder="Adrese ulaşım tarifi"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Additional Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="h-5 w-5 text-primary" />
                  <CardTitle>Ek Bilgiler</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="institution">Kurum</Label>
                  <div className="relative">
                    <Input
                      id="institution"
                      value={institutionInput}
                      onChange={e => {
                        setInstitutionInput(e.target.value);
                        setValue('institution', e.target.value);
                      }}
                      onBlur={() => setTimeout(() => setShowInstitutionDropdown(false), 150)}
                      placeholder="3 karakter yazın, kurumlar listelenecek"
                      autoComplete="off"
                    />
                    {institutionLoading && (
                      <div className="absolute right-2 top-2.5">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                      </div>
                    )}
                    {showInstitutionDropdown && institutionSuggestions.length > 0 && (
                      <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto text-sm">
                        {institutionSuggestions.map(inst => (
                          <li
                            key={inst.id}
                            className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                            onMouseDown={() => {
                              setInstitutionInput(inst.name);
                              setValue('institution', inst.name);
                              setShowInstitutionDropdown(false);
                            }}
                          >
                            <span className="font-medium">{inst.name}</span>
                            {inst.code && <span className="ml-2 text-gray-400 text-xs">{inst.code}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {showInstitutionDropdown && !institutionLoading && institutionSuggestions.length === 0 && institutionInput.length >= 3 && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-sm text-gray-400">
                        Sonuç bulunamadı
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract_info">Sözleşme</Label>
                  <Input
                    id="contract_info"
                    {...register('contract_info')}
                    placeholder="Sözleşme bilgisi"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="general_description">Açıklama</Label>
                  <Textarea
                    id="general_description"
                    {...register('general_description')}
                    placeholder="Genel açıklama"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={createOrganizationMutation.isLoading}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {createOrganizationMutation.isLoading ? 'Kaydediliyor...' : 'Organizasyonu Oluştur'}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/organizations')}
                  >
                    İptal
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewOrganizationPage; 